import { zodResolver } from '@hookform/resolvers/zod'
import { AuthRequestError } from '@ventawiz/web-kit'
import { useDeferredValue, useId, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Icon } from '@/components/ui/icon'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import {
  MIN_PLACE_SEARCH,
  useAccessCatalogue,
  useAccessRoles,
  useRequestablePlaces,
  useRequestAccess,
  useRoleOffers,
} from '@/hooks/queries/access'
import {
  ACCESS_KINDS,
  type AccessKind,
  type RequestablePlace,
  type SubjectKind,
} from '@/lib/auth/accessRequests'
import { errorMessage } from '@/lib/errors'

/** The auth service's limit on a request's reason. */
const REASON_MAX = 1000

const ORGANISATION = 'organisation'

const schema = z
  .object({
    /** The whole organisation, or a kind of place. */
    where: z.string(),
    /** The place's id, once one is chosen. */
    place: z.number().optional(),
    /** `bundle:<name>` or `permission:<code>` — one select, two kinds of thing. */
    subject: z.string().min(1, 'what'),
    reason: z.string().trim().min(1, 'reason').max(REASON_MAX, 'reasonTooLong'),
  })
  .refine((values) => values.where === ORGANISATION || values.place !== undefined, {
    path: ['place'],
    message: 'place',
  })
type RequestForm = z.infer<typeof schema>

/**
 * Ask for access, for oneself: where, a role or a permission, and why.
 *
 * VentaWiz Auth routes it to the people whose `grants:edit` reaches the place
 * and emails them; nothing changes here until one of them decides, in User
 * Management.
 *
 * Where is the whole organisation, or one place found by typing its name
 * (VentaWiz-Auth ADR 0022). For a place, the roles offered are that kind's
 * levels and custom roles, which any member may read; for the organisation,
 * the roles are offered when the service will list them for this session —
 * somebody with no access to anything usually cannot see them, and the
 * permissions are enough to ask with.
 */
export function RequestAccessDialog({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation()
  const formId = useId()
  const vocabulary = useAccessCatalogue()
  const roles = useAccessRoles()
  const request = useRequestAccess()
  const form = useForm<RequestForm>({
    resolver: zodResolver(schema),
    defaultValues: { where: ORGANISATION, subject: '', reason: '' },
  })
  // The place being typed and the one picked are a draft, not view state.
  const [search, setSearch] = useState('')
  const [picked, setPicked] = useState<RequestablePlace | null>(null)
  const where = form.watch('where')
  const kind = where === ORGANISATION ? undefined : (where as AccessKind)
  const places = useRequestablePlaces(kind ?? 'store', useDeferredValue(kind ? search : ''))
  const offers = useRoleOffers(kind ?? 'store', picked?.scope_id)

  const choosePlace = (place: RequestablePlace | null) => {
    setPicked(place)
    form.setValue('place', place?.scope_id, { shouldValidate: place !== null })
    form.setValue('subject', '')
  }

  const submit = form.handleSubmit((values) => {
    const [subjectKind = '', ...rest] = values.subject.split(':')
    request.mutate({
      subject_kind: subjectKind as SubjectKind,
      subject_ref: rest.join(':'),
      place: kind && values.place !== undefined ? { kind, id: values.place } : null,
      reason: values.reason.trim(),
    })
  })

  if (request.isSuccess) {
    return (
      <Dialog open onOpenChange={(open) => (open ? undefined : onClose())}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Icon name="check_circle" className="text-success" />
              {t('requestAccess.sentTitle')}
            </DialogTitle>
            <DialogDescription>{t('requestAccess.sent')}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" onClick={onClose}>
              {t('requestAccess.close')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    )
  }

  const errors = form.formState.errors
  const errorText = (message: string | undefined) =>
    message ? t(`requestAccess.errors.${message as 'what'}`, { max: REASON_MAX }) : undefined
  const loading = vocabulary.isPending || (kind ? offers.isPending : roles.isPending)
  const roleChoices = kind
    ? (offers.data ?? []).map((offer) => ({
        key: offer.bundle_id,
        name: offer.name,
        label: offer.label,
      }))
    : (roles.data ?? []).map((role) => ({ key: role.id, name: role.name, label: role.label }))
  const permissionChoices = (vocabulary.data ?? []).filter(
    (permission) => !kind || permission.scopes.includes(kind),
  )
  const placeError = errorText(errors.place?.message)
  const whatError = errorText(errors.subject?.message)
  const reasonError = errorText(errors.reason?.message)
  const refusal = request.error ? refusalKind(request.error) : undefined

  return (
    <Dialog open onOpenChange={(open) => (open ? undefined : onClose())}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('requestAccess.title')}</DialogTitle>
          <DialogDescription>{t('requestAccess.toOrganisation')}</DialogDescription>
        </DialogHeader>

        <form noValidate className="space-y-4" onSubmit={(event) => void submit(event)}>
          <div className="space-y-1.5">
            <Label htmlFor={`${formId}-where`}>{t('requestAccess.where')}</Label>
            <select
              id={`${formId}-where`}
              className="border-input bg-background h-9 w-full rounded-md border px-2 text-sm"
              {...form.register('where', {
                onChange: () => {
                  setSearch('')
                  choosePlace(null)
                },
              })}
            >
              <option value={ORGANISATION}>{t('requestAccess.wholeOrganisation')}</option>
              {ACCESS_KINDS.map((option) => (
                <option key={option} value={option}>
                  {t(`requestAccess.kinds.${option}`)}
                </option>
              ))}
            </select>
          </div>

          {kind ? (
            <PlacePicker
              id={`${formId}-place`}
              search={search}
              onSearch={setSearch}
              picked={picked}
              onPick={choosePlace}
              found={places.data}
              searching={places.isFetching}
              error={placeError}
            />
          ) : null}

          {kind && !picked ? null : (
            <div className="space-y-1.5">
              <Label htmlFor={`${formId}-what`}>{t('requestAccess.what')}</Label>
              {loading ? (
                <div aria-busy="true">
                  <span className="sr-only">{t('requestAccess.loading')}</span>
                  <Skeleton className="h-row w-full" />
                </div>
              ) : vocabulary.isError ? (
                <p role="status" className="bg-muted rounded-lg border p-3 text-sm">
                  {t('requestAccess.unavailable')}
                </p>
              ) : (
                <select
                  id={`${formId}-what`}
                  className="border-input bg-background h-9 w-full rounded-md border px-2 text-sm"
                  aria-invalid={whatError ? true : undefined}
                  aria-describedby={whatError ? `${formId}-what-error` : undefined}
                  {...form.register('subject')}
                >
                  <option value="">{t('requestAccess.choose')}</option>
                  {roleChoices.length > 0 ? (
                    <optgroup label={t('requestAccess.roles')}>
                      {roleChoices.map((role) => (
                        <option key={role.key} value={`bundle:${role.name}`}>
                          {role.label}
                        </option>
                      ))}
                    </optgroup>
                  ) : null}
                  <optgroup label={t('requestAccess.permissions')}>
                    {permissionChoices.map((permission) => (
                      <option key={permission.code} value={`permission:${permission.code}`}>
                        {permission.label}
                      </option>
                    ))}
                  </optgroup>
                </select>
              )}
              <FieldError id={`${formId}-what-error`} message={whatError} />
            </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor={`${formId}-reason`}>{t('requestAccess.reason')}</Label>
            <Textarea
              id={`${formId}-reason`}
              rows={3}
              aria-invalid={reasonError ? true : undefined}
              aria-describedby={`${formId}-reason-hint${reasonError ? ` ${formId}-reason-error` : ''}`}
              {...form.register('reason')}
            />
            <p id={`${formId}-reason-hint`} className="text-muted-foreground text-xs">
              {t('requestAccess.reasonHint')}
            </p>
            <FieldError id={`${formId}-reason-error`} message={reasonError} />
          </div>

          {refusal ? (
            <p role="alert" className="text-error text-sm">
              {refusal === 'pending'
                ? t('requestAccess.alreadyPending')
                : refusal === 'granted'
                  ? t('requestAccess.alreadyGranted')
                  : errorMessage(request.error, t('common.somethingWentWrong'))}
            </p>
          ) : null}

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>
              {t('requestAccess.cancel')}
            </Button>
            <Button type="submit" disabled={request.isPending || vocabulary.isError}>
              {t(request.isPending ? 'requestAccess.submitting' : 'requestAccess.submit')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

/** The two refusals with copy of their own; anything else is said in the service's words. */
function refusalKind(error: unknown): 'pending' | 'granted' | 'other' {
  if (error instanceof AuthRequestError && error.code === 'already_pending') return 'pending'
  if (error instanceof AuthRequestError && error.code === 'already_granted') return 'granted'
  return 'other'
}

function FieldError({ id, message }: { id: string; message: string | undefined }) {
  if (!message) return null
  return (
    <p id={id} role="alert" className="text-error text-xs">
      {message}
    </p>
  )
}

/**
 * A place, found by typing its name. The service answers after two
 * characters, twenty at a time; the list is buttons, so a keyboard reaches
 * every match.
 */
function PlacePicker({
  id,
  search,
  onSearch,
  picked,
  onPick,
  found,
  searching,
  error,
}: {
  id: string
  search: string
  onSearch: (value: string) => void
  picked: RequestablePlace | null
  onPick: (place: RequestablePlace | null) => void
  found: RequestablePlace[] | undefined
  searching: boolean
  error: string | undefined
}) {
  const { t } = useTranslation()
  const short = search.trim().length < MIN_PLACE_SEARCH

  if (picked) {
    return (
      <div className="space-y-1.5">
        <Label htmlFor={id}>{t('requestAccess.place')}</Label>
        <div className="flex items-center justify-between gap-2 rounded-md border px-3 py-1.5 text-sm">
          <span id={id}>{picked.label}</span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => {
              onSearch('')
              onPick(null)
            }}
          >
            {t('requestAccess.changePlace')}
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{t('requestAccess.place')}</Label>
      <Input
        id={id}
        autoComplete="off"
        value={search}
        aria-invalid={error ? true : undefined}
        aria-describedby={`${id}-hint${error ? ` ${id}-error` : ''}`}
        onChange={(event) => onSearch(event.target.value)}
      />
      <p id={`${id}-hint`} className="text-muted-foreground text-xs">
        {short
          ? t('requestAccess.placeHint', { count: MIN_PLACE_SEARCH })
          : searching
            ? t('requestAccess.searching')
            : found && found.length === 0
              ? t('requestAccess.noPlaces')
              : t('requestAccess.pickPlace')}
      </p>
      {!short && found && found.length > 0 ? (
        <ul className="divide-y rounded-md border" aria-label={t('requestAccess.matches')}>
          {found.map((place) => (
            <li key={place.scope_id}>
              <button
                type="button"
                className="hover:bg-surface-muted focus-visible:ring-ring w-full px-3 py-1.5 text-left text-sm focus-visible:ring-2 focus-visible:outline-none"
                onClick={() => onPick(place)}
              >
                {place.label}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      <FieldError id={`${id}-error`} message={error} />
    </div>
  )
}
